<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import strtotime from 'locutus/php/datetime/strtotime'
import long2ip from 'locutus/php/network/long2ip'
import PageNavigation from '@components/admin/page-navigation'
import { fetchTransferList } from '@apis/admin/withdraw'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'
import { fetchAssets } from '@apis/admin/asset'

export default {
  components: { PageNavigation },
  props: {
    withdraw_seq: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      loading: false,
      list: [],
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      s_withdraw_seq: 0,
      show_dialog: false,
      excel_headers: [
        ['seq', this.$t('withdraw_transfer_list.seq')],
        ['account_seq', this.$t('withdraw_transfer_list.account_seq')],
        ['member_seq', this.$t('withdraw_transfer_list.member_seq')],
        ['user_id', this.$t('withdraw_transfer_list.user_id')],
        ['user_name', this.$t('withdraw_transfer_list.user_name')],
        ['email_address', this.$t('withdraw_transfer_list.email_address')],
        ['account_number', this.$t('withdraw_transfer_list.account_number')],
        ['from', this.$t('withdraw_transfer_list.from')],
        ['to', this.$t('withdraw_transfer_list.to')],
        ['txid', this.$t('withdraw_transfer_list.txid')],
        ['bank_code', this.$t('withdraw_transfer_list.bank_code')],
        ['asset_title', this.$t('withdraw_transfer_list.asset_title')],
        ['asset_code', this.$t('withdraw_transfer_list.asset_code')],
        ['amount', this.$t('withdraw_transfer_list.amount')],
        ['fee_amount', this.$t('withdraw_transfer_list.fee_amount')],
        ['status', this.$t('withdraw_transfer_list.status')],
        ['message', this.$t('withdraw_transfer_list.message')],
        ['regdate', this.$t('withdraw_transfer_list.regdate')],
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('withdraw_transfer_list.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_transfer_list.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_transfer_list.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('withdraw_transfer_list.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('withdraw_transfer_list.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_transfer_list.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_transfer_list.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('withdraw_transfer_list.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
    colspan_count() {
      if (this.withdraw_seq) return 5
      else if (this.$route.params.member_seq) return 7

      return 10
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchTransferList({
        ...this.$route.params,
        ...this.$route.query,
        withdraw_seq: this.withdraw_seq,
        ...(this.withdraw_seq ? { no_paging: 1 } : {}),
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadAssets() {
      const { data } = await fetchAssets()
      this.assets = data
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('withdraw_transfer_list.msg_server_overload'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchTransferList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
          withdraw_seq: this.withdraw_seq,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('withdraw_transfer_list.withdrawal_transmission', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'status':
                return fromPairs(this.constants.withdraw.trasnfer[key])[val]
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'asset_seq')
      this.search()
    },
    date,
    fromPairs,
    long2ip,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['from', $t('withdraw_transfer_list.from')],
                      ['to', $t('withdraw_transfer_list.to')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{
                  $t('withdraw_transfer_list.statistics_exclude_members')
                }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{
                  $t('withdraw_transfer_list.withdrawal_members_excluded')
                }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.status') }}</div></th
            >
            <td>
              <ElRadioGroup v-model="search_params.status" size="mini">
                <ElRadioButton
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...constants.withdraw.transfer.status,
                  ]"
                  :key="val[0]"
                  :label="val[0]"
                  >{{ val[1] }}</ElRadioButton
                >
              </ElRadioGroup>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['last_login', $t('admin.last_login')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="
                  search_params = {}
                  search
                "
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{
      $t('withdraw_transfer_list.withdrawal_transmission')
    }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <ul v-if="!withdraw_seq" :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...assets,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.asset_seq }"
        :to="{ query: { ...$route.query, asset_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>{{ title }}</a>
      </router-link>
    </ul>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <template v-if="!withdraw_seq">
            <th scope="col" rowspan="2"
              ><div
                ><a href="#">{{ $t('admin.num') }}</a></div
              ></th
            >
            <template v-if="!$route.params.member_seq">
              <th scope="col" :class="$style.quarter_wide"
                ><div>{{ $t('admin.user_id') }}</div></th
              >
              <th scope="col" :class="$style.quarter_wide"
                ><div>{{ $t('admin.user_name') }}</div></th
              >
            </template>
            <th scope="col" colspan="2"
              ><div>{{ $t('wallet.account_number') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('withdraw_transfer_list.transmit') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('withdraw_transfer_list.tranamountsmit') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.request_amount') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.status') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.regdate') }}</div></th
          >
        </tr>
        <tr>
          <template v-if="!withdraw_seq">
            <template v-if="!$route.params.member_seq">
              <th scope="col" colspan="2" :class="$style.quarter_wide"
                ><div>{{ $t('admin.email_address') }}</div></th
              >
            </template>
            <th scope="col"
              ><div>{{ $t('admin.asset_title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.modal_asset_code') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('withdraw_transfer_list.receive') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('withdraw_transfer_list.fee_amount') }}</div></th
          >
          <th scope="col" colspan="3"
            ><div>{{ $t('withdraw_transfer_list.message') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <template v-for="(data, idx) in list">
          <tr :key="`${data.seq}-1`" :class="$style[`bg${idx % 2}`]">
            <template v-if="!withdraw_seq">
              <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
                data._no
              }}</td>
              <template v-if="!$route.params.member_seq">
                <td :class="[$style.tCenter, $style.nowrap]">{{
                  data.user_id
                }}</td>
                <td :class="[$style.tCenter, $style.nowrap]">{{
                  data.user_name
                }}</td>
              </template>
              <td colspan="2" :class="[$style.tCenter]">{{
                data.account_number
              }}</td>
              <td :class="[$style.nowrap]">{{ data.from }}</td>
            </template>
            <td :class="[$style.tRight, $style.nowrap]">{{ data.amount }}</td>
            <td :class="[$style.tRight, $style.nowrap]">{{
              data.request_amount
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              fromPairs(constants.withdraw.transfer.status)[data.status]
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.regdate))
            }}</td>
          </tr>
          <tr :key="`${data.seq}-2`" :class="$style[`bg${idx % 2}`]">
            <template v-if="!withdraw_seq">
              <template v-if="!$route.params.member_seq">
                <td colspan="2" :class="[$style.nowrap, $style.tCenter]">{{
                  data.email_address
                }}</td>
              </template>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.asset_title
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.asset_code
              }}</td>
              <td :class="[$style.nowrap]">{{ data.to }}</td>
            </template>
            <td :class="[$style.tRight, $style.nowrap]">{{
              data.fee_amount
            }}</td>
            <td colspan="3">{{ data.message }}</td>
          </tr>
        </template>
        <tr v-if="loading">
          <td :colspan="colspan_count" :class="$style.tCenter">{{
            $t('admin.loading')
          }}</td>
        </tr>
        <tr v-else-if="!list.length">
          <td :colspan="colspan_count" :class="$style.tCenter">{{
            $t('withdraw_transfer_list.msg_no_withdrawaa')
          }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading && !withdraw_seq"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
